import { memo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, IconButton, Tooltip } from "@mui/material"
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../buttons/LoadingButton"
import { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import { InitialEventState } from "./useInitialEventState"
import { CreateOrderDialogFlow, CreateOrderFlowType } from "../../createOrderTypes"
import { OrderFormStatus } from "../../mainForm/helpers"

type StyleProps = Readonly<{
    isFixed: boolean
}>
const useStyles = makeStyles<StyleProps>()((theme, { isFixed }) => ({
    fixedContainer: {
        width: "fit-content",
        position: "fixed",
        bottom: "1.5rem",
        right: "1.5rem",
        zIndex: 1000,
        textAlign: "right",
    },
    cancelButton: {
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[100],
        },
    },
    buttonWrap: {
        background: theme.palette.common.white,
        width: isFixed ? "11.25rem" : "100%",
        borderRadius: "1rem",
    },
}))

type ResourceBookingControlsProps = {
    initialEventState: InitialEventState
    onCancel: () => void
    onBook: () => void
    isFixed?: boolean
    isLoadingButton?: boolean
    flow?: CreateOrderDialogFlow["flow"]
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}

export const ResourceBookingControls = memo(
    ({
        initialEventState,
        onCancel,
        onBook,
        isFixed = true,
        isLoadingButton = false,
        flow,
        orderFormStatusRef,
    }: ResourceBookingControlsProps) => {
        const { classes, cx } = useStyles({ isFixed })
        const [{ value: event }] = useField<NullableCalendarEvent>("event")
        const { isFullInitOrder, resourceBookingId } = orderFormStatusRef.current
        const isResourceBooking = isFullInitOrder || !!resourceBookingId

        const isOutlookFlow = flow === CreateOrderFlowType.OUTLOOK
        const hasInitialId = !!initialEventState.initValues?.id
        const hasCurrentEvent = !!event

        const showUndo = isOutlookFlow
            ? initialEventState.isUpdated
            : (!hasInitialId && hasCurrentEvent) || (hasInitialId && initialEventState.isUpdated)

        return (
            <Grid
                container
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="flex-end"
                gap="1rem"
                className={cx(isFixed && classes.fixedContainer)}
            >
                {showUndo && (
                    <LocalizedStrict id="create-order-undo-button" attrs={{ title: true }}>
                        <Tooltip title="Undo all">
                            <IconButton className={classes.cancelButton} size="large" onClick={onCancel}>
                                <FontAwesomeIcon size="sm" icon={faRotateLeft} />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                )}

                <Box className={classes.buttonWrap}>
                    <LocalizedStrict id={isResourceBooking ? "create-order-update-button" : "create-order-book-button"}>
                        <StandardLoadingButton
                            loading={isLoadingButton}
                            type="button"
                            fullWidth
                            disabled={!event?.resourceIds.length}
                            onClick={onBook}
                        >
                            Book / Update
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Box>
            </Grid>
        )
    },
)
